import type { PublicCommunity } from '@tsr/shared-types';

/**
 * #547 — the PUBLIC /subscribe client. Plain `fetch` to the auth-exempt `/api/public/*` routes:
 * NO `credentials: 'include'`, NO operator headers (this is an unauthenticated resident page,
 * NOT the operator ./api client). Kept separate from ./api on purpose.
 */

export interface RequestPinResult {
  ok: boolean;
  /** present when ok: the masked number the code was texted to, + the client-side timers. */
  phone_masked?: string;
  expires_in_minutes?: number;
  resend_cooldown_seconds?: number;
  /** present when !ok: a machine code (e.g. 'invalid_phone' | 'community_unavailable'). */
  error?: string;
  message?: string;
}

export interface ConfirmPinResult {
  ok: boolean;
  confirmed: boolean;
  error?: string;
}

export async function fetchPublicCommunities(signal?: AbortSignal): Promise<PublicCommunity[]> {
  const res = await fetch('/api/public/communities', { signal });
  if (!res.ok) throw new Error(`fetchPublicCommunities failed ${res.status}`);
  return ((await res.json()) as { communities: PublicCommunity[] }).communities;
}

export async function requestResidentPin(
  communityId: string,
  phone: string,
  signal?: AbortSignal,
): Promise<RequestPinResult> {
  const res = await fetch('/api/public/subscribe/request-pin', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ community_id: communityId, phone }),
    signal,
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    return { ok: false, error: String(data.error ?? 'error'), message: data.message as string | undefined };
  }
  return {
    ok: true,
    phone_masked: data.phone_masked as string | undefined,
    expires_in_minutes: data.expires_in_minutes as number | undefined,
    resend_cooldown_seconds: data.resend_cooldown_seconds as number | undefined,
  };
}

export async function confirmResidentPin(
  communityId: string,
  phone: string,
  pin: string,
  signal?: AbortSignal,
): Promise<ConfirmPinResult> {
  const res = await fetch('/api/public/subscribe/confirm-pin', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ community_id: communityId, phone, pin }),
    signal,
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) return { ok: false, confirmed: false, error: String(data.error ?? 'error') };
  return { ok: true, confirmed: data.confirmed === true };
}

export async function unsubscribeResident(
  body: { unsubscribe_token: string } | { community_id: string; phone: string },
  signal?: AbortSignal,
): Promise<{ ok: boolean }> {
  const res = await fetch('/api/public/subscribe/unsubscribe', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  return { ok: res.ok };
}
